// Global keyboard shortcuts (SPEC §11): T tasks board, P calendar, A approvals, B building, 1–9 floors,
// PageUp/PageDown the floor above / below (like the building: 01 is the ground), D dark mode, ? help,
// Esc close. Panels may push a scope that
// sees keys first (e.g. approvals: A approve, R reject, J/K next/prev).
import { h, kbd, isEditable } from '../dom.js';
import { t } from '../i18n/fr.js';

/** The shortcuts shown in the help dialog. */
export const SHORTCUTS = [
  { keys: ['T'], label: 'keys.board' },
  { keys: ['P'], label: 'keys.calendar' },
  { keys: ['A'], label: 'keys.approvals' },
  { keys: ['B'], label: 'keys.building' },
  { keys: ['1', '…', '9'], label: 'keys.floors' },
  { keys: ['Page ↑', 'Page ↓'], label: 'keys.pages' },
  { keys: ['D'], label: 'keys.theme' },
  { keys: ['Esc'], label: 'keys.escape' },
  { keys: ['?'], label: 'keys.help' },
];

/**
 * Maps a keyboard event to an action name (pure, testable):
 * 'board' | 'calendar' | 'approvals' | 'building' | 'theme' | 'help' | 'floorUp' | 'floorDown' |
 * 'floor:<index>' | 'escape' | null (PageUp = the floor above, i.e. the higher number). Shortcuts are ignored while typing, with Ctrl/Cmd/Alt, or when
 * an overlay is open (except Escape).
 * @param {{key: string, ctrlKey?: boolean, metaKey?: boolean, altKey?: boolean, isComposing?: boolean}} e
 * @param {{editable?: boolean, overlayOpen?: boolean}} [ctx]
 */
export function resolveShortcut(e, { editable = false, overlayOpen = false } = {}) {
  if (!e || e.isComposing || e.ctrlKey || e.metaKey || e.altKey) return null;
  if (e.key === 'Escape') return 'escape';
  if (editable || overlayOpen) return null;
  switch (e.key) {
    case 't': case 'T': return 'board';
    case 'p': case 'P': return 'calendar';
    case 'a': case 'A': return 'approvals';
    case 'b': case 'B': return 'building';
    case 'd': case 'D': return 'theme';
    case '?': return 'help';
    case 'PageUp': return 'floorUp';
    case 'PageDown': return 'floorDown';
    default: break;
  }
  if (/^[1-9]$/.test(e.key)) return `floor:${Number(e.key) - 1}`;
  return null;
}

/**
 * Installs the global keydown listener.
 * handlers: { board, calendar, approvals, building, theme, help, floorUp, floorDown,
 *             floor(index), escape }
 * @param {{overlay?: {count(): number}, handlers?: object}} opts
 * @returns {{pushScope(fn: (e: KeyboardEvent, info: {editable: boolean}) => boolean|void): () => void,
 *   setHandlers(h: object): void, destroy(): void}}
 */
export function createKeys({ overlay, handlers = {} } = {}) {
  const scopes = [];
  const hs = { ...handlers };

  function onKeydown(e) {
    if (e.defaultPrevented) return;
    const editable = isEditable(e.target);
    for (let i = scopes.length - 1; i >= 0; i -= 1) {
      let handled = false;
      try { handled = scopes[i](e, { editable }) === true; } catch (err) { console.error(err); }
      if (handled) { e.preventDefault(); return; }
    }
    const action = resolveShortcut(e, { editable, overlayOpen: (overlay?.count?.() || 0) > 0 });
    if (!action) return;
    if (action === 'escape') {
      if (overlay?.count?.()) return; // the overlay closes itself
      if (editable) { e.target.blur(); return; }
      hs.escape?.();
      return;
    }
    const fn = action.startsWith('floor:') ? (hs.floor && (() => hs.floor(Number(action.slice(6))))) : hs[action];
    if (typeof fn === 'function') {
      e.preventDefault();
      fn();
    }
  }

  document.addEventListener('keydown', onKeydown);
  return {
    pushScope(fn) {
      scopes.push(fn);
      return () => { const i = scopes.indexOf(fn); if (i >= 0) scopes.splice(i, 1); };
    },
    setHandlers(next) { Object.assign(hs, next); },
    destroy() { document.removeEventListener('keydown', onKeydown); scopes.length = 0; },
  };
}

/** Opens the shortcuts help dialog. */
export function openShortcutsHelp(overlay) {
  const rows = SHORTCUTS.map((s) => h('div', { class: 'shortcut-row' },
    h('span', { class: 'shortcut-keys' }, ...s.keys.map((k) => (k === '…' ? h('span', { class: 'muted' }, '–') : kbd(k)))),
    h('span', { class: 'shortcut-label' }, t(s.label))));
  return overlay.openModal({ title: t('keys.title'), size: 'sm', content: h('div', { class: 'shortcut-list' }, rows) });
}
